import {
  Outlet,
  RouterProvider,
  createBrowserRouter,
  redirect,
  useLocation,
  useNavigate,
} from "react-router-dom";
import {
  Disputes,
  Home,
  NewOrders,
  Onboarding,
  PastOrders,
  Stake,
  Stats,
} from "./pages";
import {
  SmartWalletProvider,
  USDTContextProvider,
  MerchantContextProvider,
  OrderProcessorContextProvider,
  useMerchant,
  useSmartWallet,
} from "./contexts";
import { Toaster } from "./components/ui/toaster";
import { LocalStorageKeys } from "./constants";
import { ThirdwebProvider } from "thirdweb/react";
import ArchivedOrders from "./pages/ArchivedOrders";
import { UpiCheckWrapper } from "./components/upi-check";
import { useEffect, useState } from "react";
import InstallModal from "./components/InstallModal";

const Layout = () => {
  interface BeforeInstallPromptEvent extends Event {
    readonly platforms: string[];
    readonly userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
    prompt(): Promise<void>;
  }

  const [deferredPrompt, setDeferredPrompt] =
    useState<BeforeInstallPromptEvent | null>(null);
  const [isInstallPromptVisible, setInstallPromptVisible] = useState(false);

  useEffect(() => {
    const handleBeforeInstallPrompt = (e: any) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setInstallPromptVisible(true);
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener(
        "beforeinstallprompt",
        handleBeforeInstallPrompt,
      );
    };
  }, []);

  const handleInstallClick = () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      deferredPrompt.userChoice.then((choiceResult) => {
        if (choiceResult.outcome === "accepted") {
          console.log("User accepted the install prompt");
        } else {
          console.log("User dismissed the install prompt");
        }
        setDeferredPrompt(null);
        setInstallPromptVisible(false);
      });
    }
  };

  const closeModal = () => {
    setInstallPromptVisible(false);
  };

  return (
    <div className="font-satoshi flex h-screen w-full items-center justify-center bg-radial-gradient">
      <ThirdwebProvider>
        <SmartWalletProvider>
          <USDTContextProvider>
            <MerchantContextProvider>
              <OrderProcessorContextProvider>
                <ProtectedRoutes>
                  <UpiCheckWrapper>
                    <Toaster />
                    <Outlet />
                  </UpiCheckWrapper>
                  {/* Show Install Modal */}
                  {isInstallPromptVisible && (
                    <InstallModal
                      onClose={closeModal}
                      onInstall={handleInstallClick}
                    />
                  )}
                </ProtectedRoutes>
              </OrderProcessorContextProvider>
            </MerchantContextProvider>
          </USDTContextProvider>
        </SmartWalletProvider>
      </ThirdwebProvider>
    </div>
  );
};

const onboardingRedirection = async () => {
  const isLoggedIn = Boolean(localStorage.getItem(LocalStorageKeys.LOGGED_IN));

  if (!isLoggedIn) {
    return redirect("/onboarding");
  }
  return null;
};

const ProtectedRoutes = ({ children }: any) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isLoggedIn } = useSmartWallet();
  const { merchantDetails } = useMerchant();

  if (!merchantDetails) return children;
  const approvedRoutes = ["/stake", "/onboarding"]; // ONLY ROUTES ALLOWED IN ONLY {APPROVED STATE}
  const noStateRoutes = ["/onboarding"]; // ONLY ROUTES ALLOWED IN {NOT APPROVED AND NOT REGISTERED STATE}

  // ALLOWED ALL THE ROUTES AS THIS IS REGISTERED STATE
  if (merchantDetails?.isRegistered && merchantDetails?.isApproved) {
    if (noStateRoutes.includes(location.pathname) && isLoggedIn)
      return navigate("/");
    return children;
  }
  if (merchantDetails?.isApproved) {
    if (!approvedRoutes.includes(location.pathname)) return navigate("/stake");
    return children;
  }
  if (!merchantDetails?.isApproved) {
    if (!noStateRoutes.includes(location.pathname))
      return navigate("/onboarding");
    return children;
  }
};

const router = createBrowserRouter([
  {
    path: "/",
    element: <Layout />,
    children: [
      {
        path: "/",
        Component: Home,
        loader: onboardingRedirection,
      },
      {
        path: "/onboarding",
        Component: Onboarding,
      },
      {
        path: "/stats",
        Component: Stats,
        loader: onboardingRedirection,
      },
      {
        path: "/stake",
        Component: Stake,
        loader: onboardingRedirection,
      },
      {
        path: "/disputes",
        Component: Disputes,
        loader: onboardingRedirection,
      },
      {
        path: "/past-orders",
        Component: PastOrders,
        loader: onboardingRedirection,
      },
      {
        path: "/new-orders",
        Component: NewOrders,
        loader: onboardingRedirection,
      },
      {
        path: "*",
        Component: Home,
        loader: onboardingRedirection,
      },
      {
        path: "/archived-orders",
        Component: ArchivedOrders,
        loader: onboardingRedirection,
      },
    ],
  },
]);

const App = () => <RouterProvider router={router} />;
export default App;
